import AppDrawer, { drawerWidth } from '@/components/AppDrawer';
import AppProfileSelect from '@/components/AppProfileSelect';
import AppSearch from '@/components/AppSearch';
import AppToolbar from '@/components/AppToolbar';
import AuthNavigation from '@/components/AuthNavigation';
import Logo from '@/components/Logo';
import Navigation from '@/components/Navigation';
import ProfileNavigation from '@/components/ProfileNavigation';
import UserMenu from '@/components/UserMenu';
import {
  OrganizationFieldsFragment,
  ProfileFieldsFragment,
  UserFieldsFragment,
} from '@/generated/graphql';
import { Box, Toolbar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { find } from 'lodash';
import React, { useState } from 'react';

const useStyles = makeStyles((theme) => ({
  layout: {
    display: 'flex',
    flexDirection: 'column',
  },
  navigation: {
    display: 'flex',
    flexDirection: 'row',
  },
  content: {
    width: `calc(100% - ${drawerWidth}px)`,
    flex: 1,
  },
  toolbarSelector: {
    marginLeft: theme.spacing(2),
    width: 250,
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  drawerSelector: {
    padding: theme.spacing(1, 2, 0),
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
    },
  },
  avatar: {
    marginRight: theme.spacing(-1.5),
    [theme.breakpoints.up('lg')]: {
      marginRight: 0,
    },
  },
  fixed: {
    [theme.breakpoints.up('lg')]: {
      width: `calc(100% - ${drawerWidth}px)`,
    },
    width: '100%',
    position: 'fixed',
    bottom: 0,
    top: 56,
    display: 'flex',
    flexDirection: 'column',
    [`${theme.breakpoints.up('xs')} and (orientation: landscape)`]: {
      top: 48,
    },
    [theme.breakpoints.up('sm')]: {
      top: 64,
    },
  },
}));

interface AppFrameProps {
  loading?: boolean;
  user?:
    | (UserFieldsFragment & {
        profiles: (ProfileFieldsFragment & {
          organization?: OrganizationFieldsFragment | null;
        })[];
      })
    | null;
  pid?: string;
  fixed?: boolean;
  /** A notice above the page content, such as the MFA grace banner. */
  banner?: React.ReactNode;
  children: React.ReactNode;
}

export default function AppFrame({
  loading = false,
  user = null,
  pid = '',
  fixed = false,
  banner = null,
  children,
}: AppFrameProps): JSX.Element {
  const classes = useStyles();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const profiles = user?.profiles ?? [];
  const profile = find(profiles, { pid });
  const showProfileSelector = !loading && Boolean(user && profiles.length);

  function handleDrawerOpen() {
    setDrawerOpen(true);
  }

  function handleDrawerClose() {
    setDrawerOpen(false);
  }

  return (
    <div className={classes.layout}>
      <AppToolbar onDrawerOpen={handleDrawerOpen}>
        <Box display="flex" flex={1}>
          {showProfileSelector && (
            <>
              <div className={classes.toolbarSelector}>
                <AppProfileSelect profiles={profiles} value={pid} />
              </div>
              <Box flex="1 1 auto" maxWidth={400} ml={1}>
                <AppSearch />
              </Box>
            </>
          )}
        </Box>
        <div className={classes.avatar}>
          <UserMenu user={user} />
        </div>
      </AppToolbar>

      <div className={classes.navigation}>
        <AppDrawer
          open={drawerOpen}
          onOpen={handleDrawerOpen}
          onClose={handleDrawerClose}
        >
          <Toolbar disableGutters>
            <Box px={2} display="flex" alignItems="center">
              <Logo />
            </Box>
          </Toolbar>

          {showProfileSelector && (
            <div className={classes.drawerSelector}>
              <AppProfileSelect profiles={profiles} value={pid} native />
            </div>
          )}

          {!loading && (
            <Navigation>
              {!user && <AuthNavigation />}
              {!!profile && (
                <ProfileNavigation
                  profile={profile}
                  onClick={handleDrawerClose}
                />
              )}
            </Navigation>
          )}
        </AppDrawer>

        {!loading && (
          <Box className={classes.content}>
            <Toolbar />
            {/* Inside the fixed box, so a fixed page cannot cover it. */}
            <Box className={clsx({ [classes.fixed]: fixed })}>
              {banner}
              {children}
            </Box>
          </Box>
        )}
      </div>
    </div>
  );
}
